/**
 * signUpWithRedirect.ts
 *
 * Navigates directly to Cognito's /signup page (the "Create account" form)
 * rather than /oauth2/authorize which lands on the login screen.
 *
 * Amplify's signInWithRedirect() always redirects through /oauth2/authorize →
 * /login, even when the user clicked "Register". This utility replicates
 * Amplify's PKCE setup manually, stores the verifier and state in localStorage
 * under the exact keys Amplify expects (so it can complete the code exchange on
 * redirect-back), then sends the browser to /signup instead of /oauth2/authorize.
 *
 * localStorage key format used by Amplify v6:
 *   CognitoIdentityServiceProvider.<userPoolClientId>.oauthPKCE
 *   CognitoIdentityServiceProvider.<userPoolClientId>.oauthState
 *   CognitoIdentityServiceProvider.<userPoolClientId>.inflightOAuth
 */

import { Amplify } from 'aws-amplify'

const STORAGE_PREFIX = 'CognitoIdentityServiceProvider'
const CHARSET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

function generateRandomString(length: number): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => CHARSET[b % CHARSET.length]).join('')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  // Base64url-encode without padding — matches Amplify's format.
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/** Mirror of Amplify's getRedirectUrl() — picks the configured redirect URI that
 *  best matches the current window origin. */
function pickRedirectUrl(redirects: string[]): string {
  const origin = window.location.origin
  const hostname = window.location.hostname
  return (
    redirects.find((r) => r.startsWith(origin)) ??
    redirects.find((r) => r.includes(hostname)) ??
    redirects[0]
  )
}

/**
 * Generate PKCE state, store it where Amplify expects it, then navigate the
 * browser to the Cognito /signup endpoint so the user lands on the
 * "Create account" form rather than the "Sign in" tab.
 */
export async function signUpWithRedirect(): Promise<void> {
  const authConfig = Amplify.getConfig()?.Auth?.Cognito
  if (!authConfig) {
    console.error('[signUpWithRedirect] Amplify not configured')
    return
  }
  const oauth = authConfig.loginWith?.oauth
  if (!oauth) {
    console.error('[signUpWithRedirect] OAuth not configured')
    return
  }

  const { domain, scopes, redirectSignIn, responseType = 'code' } = oauth

  if (!domain) {
    console.error('[signUpWithRedirect] OAuth domain is not configured')
    return
  }

  const scopeList = Array.isArray(scopes) ? scopes : scopes ? [scopes] : []
  const redirectUris = Array.isArray(redirectSignIn)
    ? redirectSignIn.filter((r): r is string => typeof r === 'string')
    : typeof redirectSignIn === 'string'
      ? [redirectSignIn]
      : []

  if (redirectUris.length === 0) {
    console.error('[signUpWithRedirect] No redirectSignIn URL configured')
    return
  }

  const clientId = authConfig.userPoolClientId
  if (!clientId) {
    console.error('[signUpWithRedirect] userPoolClientId is not configured')
    return
  }
  const redirectUri = pickRedirectUrl(redirectUris)

  // Generate PKCE parameters.
  const verifier = generateRandomString(128)
  const challenge = await generateCodeChallenge(verifier)
  const state = generateRandomString(32)

  // Persist in localStorage using Amplify's key format so the library can
  // complete the PKCE code-exchange when the browser is redirected back.
  const keyPrefix = `${STORAGE_PREFIX}.${clientId}`
  localStorage.setItem(`${keyPrefix}.inflightOAuth`, 'true')
  localStorage.setItem(`${keyPrefix}.oauthState`, state)
  localStorage.setItem(`${keyPrefix}.oauthPKCE`, verifier)

  // Build the /signup URL.  All OAuth params are identical to what Amplify
  // sends to /oauth2/authorize — only the path differs.
  const params = new URLSearchParams()
  params.append('redirect_uri', redirectUri)
  params.append('response_type', responseType)
  params.append('client_id', clientId)
  params.append('identity_provider', 'COGNITO')
  params.append('scope', scopeList.join(' '))
  params.append('state', state)
  params.append('code_challenge', challenge)
  params.append('code_challenge_method', 'S256')

  window.location.href = `https://${domain}/signup?${params.toString()}`
}
